import { useCallback, useEffect, useRef, useState } from 'react'
import { Check, Settings } from 'lucide-react'
import { erfuelltRecht, type ActualTimeEntry, geldText } from '@symphenia/shared-types'
import { useTranslation } from 'react-i18next'
import { FilterBar } from '@/components/list/FilterBar'
import { ExportButton } from '@/components/ui/ExportButton'
import { HelpPopover } from '@/components/ui/HelpPopover'
import { LayoutDefaultActions } from '@/components/ui/LayoutDefaultActions'
import type { ActiveFilter, FilterFieldDef } from '@/components/list/list.types'
import { apiErrorText } from '@/lib/api-error'
import { useAuthStore } from '@/lib/auth.store'
import { loadUiSetting, markUiSettingApplied, saveUiSetting } from '@/lib/ui-settings'
import { useTerminology } from '@/lib/useTerminology'
import { actualEntriesExportRequest, fetchActualEntries, type ActualEntriesFilter, type ActualEntriesResult } from '../api/project-controlling.api'
import { alsTag } from '../project-labels'

const zahl = (n: number | null | undefined) => (n === null || n === undefined ? '—' : n.toLocaleString(navigator.language, { maximumFractionDigits: 2 }))
/** Betrag im Geldformat (`geld.ts`); leer → „—". */
const geld = (n: number | null | undefined) => geldText(n) || '—'

const FILTER: FilterFieldDef[] = [
  { key: 'from', label: 'ab', type: 'date', category: 'core', operators: ['is'] },
  { key: 'until', label: 'bis (Stichtag)', type: 'date', category: 'core', operators: ['is'] },
  { key: 'onlyUnchecked', label: 'nur ungeprüft', type: 'boolean', category: 'core', operators: ['is'] },
  { key: 'onlyUnbilled', label: 'nur ohne Abrechnung', type: 'boolean', category: 'core', operators: ['is'] },
]

interface Spalte {
  key: string
  label: string
  rechts?: boolean
  preis?: boolean
}

const COLUMN_LAYOUT_KEY = 'columnbar_layout_project_actuals'
const STANDARD = ['date', 'user', 'qualification', 'step', 'phase', 'hours', 'cost', 'billing', 'status']

function ladeSpalten(): string[] {
  const g = loadUiSetting<{ columnKeys?: string[] } | null>(COLUMN_LAYOUT_KEY, null)
  return g?.columnKeys?.length ? g.columnKeys : STANDARD
}

const STATUS: Record<string, string> = { SAVED: 'gespeichert', SYNCED: 'übertragen', REVIEWED: 'geprüft', APPROVED: 'genehmigt' }

/**
 * AP-35 Detailblatt 8 2.1 — **Arbeitszeiten** einer Position oder eines Projekts.
 *
 * Nur lesen: Die Buchung gehört der Zeiterfassung. Ungeprüfte stehen gedämpft
 * da, wenn der Mandant „nur geprüfte" eingestellt hat — sichtbar, aber nicht in
 * der Summe. Beträge nur mit Preisrecht. Export mit Mailweg seit Phase K.
 */
export function ActualEntriesList({ projectId, positionId }: { projectId: string; positionId: string | null }) {
  const terms = useTerminology()
  const { user } = useAuthStore()
  const preise = erfuelltRecht('projects.price.view', user?.permissions)
  const darfExport = erfuelltRecht('projects.export.run', user?.permissions)
  const { t: uebersetze } = useTranslation()

  const ALLE: Spalte[] = [
    { key: 'date', label: 'Datum' },
    { key: 'time', label: 'von–bis' },
    { key: 'user', label: 'Mitarbeiter' },
    { key: 'qualification', label: 'Qualifikation' },
    { key: 'position', label: terms.position },
    { key: 'step', label: 'Arbeitsschritt' },
    { key: 'phase', label: 'Phase' },
    { key: 'hours', label: 'Stunden', rechts: true },
    { key: 'costRate', label: 'Kostensatz', rechts: true, preis: true },
    { key: 'cost', label: 'Kosten', rechts: true, preis: true },
    { key: 'salesRate', label: 'Verkaufssatz', rechts: true, preis: true },
    { key: 'billing', label: 'Lieferschein / Rechnung' },
    { key: 'status', label: 'Status' },
  ]

  const [filterWerte, setFilterWerte] = useState<Record<string, ActiveFilter>>({})
  const [spalten, setSpalten] = useState<string[]>(ladeSpalten)
  const [konfig, setKonfig] = useState(false)
  const [seite, setSeite] = useState(1)
  const [ergebnis, setErgebnis] = useState<ActualEntriesResult | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const filter = (werte: Record<string, ActiveFilter>, s: number): ActualEntriesFilter => {
    const f: ActualEntriesFilter = { page: s, limit: 100 }
    for (const [k, a] of Object.entries(werte)) {
      if (!a.value) continue
      if (k === 'from') f.from = a.value.slice(0, 10)
      if (k === 'until') f.until = a.value.slice(0, 10)
      if (k === 'onlyUnchecked') f.onlyUnchecked = a.value === 'true'
      if (k === 'onlyUnbilled') f.onlyUnbilled = a.value === 'true'
    }
    return f
  }

  const laden = useCallback(
    async (werte: Record<string, ActiveFilter>, s: number) => {
      try {
        setErgebnis(await fetchActualEntries(projectId, positionId, filter(werte, s)))
        setFehler(null)
      } catch (e) {
        setFehler(apiErrorText(e, 'Die Arbeitszeiten konnten nicht geladen werden.'))
      }
    },
    [projectId, positionId],
  )

  useEffect(() => {
    void laden(filterWerte, seite)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [laden, seite])

  const gezeigt = spalten.map((k) => ALLE.find((c) => c.key === k)).filter((c): c is Spalte => !!c && (!c.preis || preise) && (c.key !== 'position' || !positionId))
  const hinzu = ALLE.filter((c) => !spalten.includes(c.key) && (!c.preis || preise) && (c.key !== 'position' || !positionId))
  const spaltenAendern = (keys: string[]) => {
    setSpalten(keys)
    saveUiSetting(COLUMN_LAYOUT_KEY, { columnKeys: keys, columnWidths: {} })
  }

  const zelle = (e: ActualTimeEntry, k: string): React.ReactNode => {
    switch (k) {
      case 'date':
        return alsTag(e.date)
      case 'time':
        return e.from && e.to ? `${e.from}–${e.to}` : '—'
      case 'user':
        return e.userName ?? '—'
      case 'qualification':
        return e.qualificationName ?? '—'
      case 'position':
        return e.positionNumber ?? '—'
      case 'step':
        return e.stepNumber ? `${e.stepNumber} ${e.stepName ?? ''}` : <span className="text-muted-foreground">nicht zugeordnet</span>
      case 'phase':
        return (
          <>
            {e.phaseName ?? '—'}
            {e.isAssembly && <span className="ml-1 text-xs text-muted-foreground">Montage</span>}
          </>
        )
      case 'hours':
        return zahl(e.hours)
      case 'costRate':
        return e.costRate == null ? <span className="text-amber-700 dark:text-amber-400">ohne Satz</span> : geld(e.costRate)
      case 'cost':
        return geld(e.costAmount)
      case 'salesRate':
        return geld(e.salesRate)
      case 'billing':
        return e.billing ? [e.billing.deliveryNoteNumber, e.billing.invoiceNumber].filter(Boolean).join(' · ') || 'Verkaufszeile' : e.siteReportNumber ? `RB ${e.siteReportNumber}` : '—'
      case 'status':
        return STATUS[e.status] ?? e.status
      default:
        return null
    }
  }

  const t = ergebnis?.totals
  return (
    <div className="space-y-3">
      <FilterBar
        availableFields={FILTER}
        value={filterWerte}
        onChange={(w) => {
          setFilterWerte(w)
          setSeite(1)
          if (timer.current) clearTimeout(timer.current)
          timer.current = setTimeout(() => void laden(w, 1), 400)
        }}
        layoutKey="project-actuals"
        defaultFieldKeys={['from', 'until', 'onlyUnchecked']}
      />
      {darfExport && (
        <div className="flex justify-end">
          <ExportButton
            request={(format, orientation) => {
              const { page: _seite, limit: _grenze, ...f } = filter(filterWerte, 1)
              return actualEntriesExportRequest(projectId, positionId, { ...f, format, orientation }, gezeigt.map((c) => ({ key: c.key, label: c.label })))
            }}
            mail={{
              source: 'EXPORT_PROJECT_ACTUALS',
              title: uebersetze('mailExport.titles.EXPORT_PROJECT_ACTUALS'),
              subject: uebersetze('mailExport.titles.EXPORT_PROJECT_ACTUALS'),
            }}
          />
        </div>
      )}
      {fehler && <p className="text-sm text-destructive">{fehler}</p>}
      <div className="relative overflow-hidden rounded-lg border border-border bg-card shadow-sm">
        <button
          type="button"
          onClick={() => setKonfig((v) => !v)}
          className={`absolute right-1.5 top-1.5 z-10 flex h-6 w-6 items-center justify-center rounded-md ${konfig ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'}`}
          title="Spalten konfigurieren"
        >
          {konfig ? <Check size={14} /> : <Settings size={14} />}
        </button>
        <HelpPopover topic="columns" className="absolute right-8 top-1.5" />
        {konfig && (
          <div className="flex flex-wrap items-center gap-2 border-b border-border bg-muted/30 px-3 py-2 pr-16 text-xs">
            <LayoutDefaultActions
              settingKey={COLUMN_LAYOUT_KEY}
              currentValue={{ columnKeys: spalten, columnWidths: {} }}
              onReset={(v) => {
                const keys = (v as { columnKeys?: string[] } | null)?.columnKeys ?? STANDARD
                markUiSettingApplied(COLUMN_LAYOUT_KEY, { columnKeys: keys, columnWidths: {} })
                setSpalten(keys)
              }}
            />
            {hinzu.length > 0 && (
              <select
                className="rounded border border-border bg-background px-1 py-0.5"
                defaultValue=""
                onChange={(ev) => {
                  if (ev.target.value) spaltenAendern([...spalten, ev.target.value])
                  ev.target.value = ''
                }}
              >
                <option value="">+ Spalte</option>
                {hinzu.map((c) => (
                  <option key={c.key} value={c.key}>
                    {c.label}
                  </option>
                ))}
              </select>
            )}
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-muted/50">
              <tr>
                {gezeigt.map((c) => (
                  <th key={c.key} className={`px-3 py-1.5 font-medium ${c.rechts ? 'text-right' : 'text-left'}`}>
                    {c.label}
                    {konfig && (
                      <button type="button" className="ml-1 text-muted-foreground hover:text-destructive" onClick={() => spaltenAendern(spalten.filter((k) => k !== c.key))}>
                        ✕
                      </button>
                    )}
                  </th>
                ))}
                <th className="w-14" />
              </tr>
            </thead>
            <tbody>
              {ergebnis && ergebnis.data.length === 0 && (
                <tr>
                  <td colSpan={gezeigt.length + 1} className="px-4 py-8 text-center text-muted-foreground">
                    Keine Buchungen.
                  </td>
                </tr>
              )}
              {ergebnis?.data.map((e) => (
                <tr key={e.timeEntryId} className={`border-b border-border last:border-0 ${t?.checkedOnly && !e.isChecked ? 'opacity-50' : ''}`}>
                  {gezeigt.map((c) => (
                    <td key={c.key} className={`px-3 py-1.5 ${c.rechts ? 'text-right tabular-nums' : ''}`}>
                      {zelle(e, c.key)}
                    </td>
                  ))}
                  <td />
                </tr>
              ))}
            </tbody>
            {t && (
              <tfoot className="border-t-2 border-border bg-muted/40 text-xs font-medium">
                <tr>
                  <td colSpan={gezeigt.length + 1} className="px-3 py-1.5">
                    {zahl(t.hours)} h{t.checkedOnly ? ' geprüft' : ''}
                    {t.uncheckedHours > 0 && ` · davon ${t.checkedOnly ? 'nicht gezählt' : 'ungeprüft'} ${zahl(t.uncheckedHours)} h`}
                    {preise && t.costAmount !== undefined && ` · Kosten ${geld(t.costAmount)}`}
                    {ergebnis && ergebnis.meta.totalPages > 1 && (
                      <span className="ml-4">
                        <button type="button" disabled={seite <= 1} className="underline disabled:opacity-40" onClick={() => setSeite((s) => s - 1)}>
                          zurück
                        </button>{' '}
                        Seite {seite} von {ergebnis.meta.totalPages}{' '}
                        <button type="button" disabled={seite >= ergebnis.meta.totalPages} className="underline disabled:opacity-40" onClick={() => setSeite((s) => s + 1)}>
                          weiter
                        </button>
                      </span>
                    )}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  )
}
